import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { WorkAndIncomeCard } from "@/blocks/WorkAndIncomeCard";
import { VnBox } from "@/components/VnBox";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import { useCesSurvey } from "@/hooks/useCesSurvey";
import { CesSurveyPrompt } from "@/interactors/CesSurveyPrompt";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { hasIncomeConfig } from "@/utils/householdMember";

export const ApplicationWorkAndIncomeScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();
  const { showCesSurveyThenContinue, cesSurveyProps } = useCesSurvey({
    triggerPoint: "INCOME_ADDED",
  });

  const { data: householdMembers = [] } = useListHouseholdMembersQuery();
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const { totalMembersCount, completedMembersCount } = useMemo(() => {
    const total = householdMembers.length;
    const completed = householdMembers.filter(member =>
      hasIncomeConfig(member),
    ).length;
    return { totalMembersCount: total, completedMembersCount: completed };
  }, [householdMembers]);

  const isNextDisabled = completedMembersCount !== totalMembersCount;

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "WORK_AND_INCOME",
        },
      },
      {
        onSuccess: () => {
          showCesSurveyThenContinue(() => {
            setApplicationCurrentStep(step =>
              Math.min(step + 1, applicationTotalSteps),
            );

            router.navigate({
              pathname: "/(authed)/(modal)/application/household-expenses",
            });
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      titleI18nKey="native:applicationWorkAndIncomeScreen.title"
      descriptionI18nKey="native:applicationWorkAndIncomeScreen.description">
      <VnBox className="gap-4">
        {householdMembers.map(member => {
          const isExpanded = expandedMemberId === member.id;
          return (
            <WorkAndIncomeCard
              key={member.id}
              member={member}
              isExpanded={isExpanded}
              onToggleExpand={() => handleToggleExpand(member.id)}
              onClose={handleClose}
            />
          );
        })}
        <VnText
          variant="lg"
          className="text-muted-foreground text-end"
          i18nKey="common:progressIndicator"
          i18nOptions={{
            progress: completedMembersCount,
            total: totalMembersCount,
          }}
        />
      </VnBox>

      <CesSurveyPrompt {...cesSurveyProps} />
    </ApplicationScreenLayout>
  );
};
